import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formApi } from '../../services';
import { Form } from '../../types';
import { SortOption, TypeFilter } from './constants';

export const FORMS_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

export const useForms = (tenantId: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const [forms, setForms] = useState<Form[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [sort, setSort] = useState<SortOption>('date');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const filtersKey = `${debouncedSearch}|${type}|${sort}`;
  const prevFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (prevFiltersKey.current !== filtersKey && page !== 1) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', '1');
        return next;
      });
    }
    prevFiltersKey.current = filtersKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const result = await formApi.getForms(tenantId, page, FORMS_PAGE_SIZE, {
        search: debouncedSearch,
        formType: type === 'all' ? undefined : type,
        sort,
      });
      if (Array.isArray(result)) {
        setForms(result);
        setTotalCount(result.length);
      } else {
        setForms(result?.rows || []);
        setTotalCount(result?.count || 0);
      }
    } catch (error) {
      setForms([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, page, debouncedSearch, type, sort]);

  const handlePageChange = (value: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(value));
      return next;
    });
  };

  const removeFormLocally = (formId: string) => {
    setForms((current) => current.filter((form) => form.id !== formId));
    setTotalCount((count) => Math.max(0, count - 1));
  };

  const isFiltered = search.trim() !== '' || type !== 'all';

  const clearFilters = () => {
    setSearch('');
    setType('all');
  };

  return {
    forms,
    totalCount,
    loading,
    initialLoading,
    page,
    handlePageChange,
    removeFormLocally,
    refetch: fetchForms,
    search,
    setSearch,
    type,
    setType,
    sort,
    setSort,
    isFiltered,
    clearFilters,
  };
};
